import { toast } from "react-toastify";
import api from "@/api/api";
import i18n from '../i18n';
import type { MultiLangItem } from "@/types";

export const USER_ROLES = {
    ADMIN: 1,
    USER: 0,
} as const;

export const MULTIPLE_LANG = {
    VI: 'vi',
    EN: 'en',
    ZH: 'zh'
} as const;

export function objectToFormData(obj: Record<string, any>): FormData {
    const formData = new FormData();

    const flattenArray = (arr: any[]): any[] => arr.flat(Infinity);
    Object.entries(obj).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            flattenArray(value).forEach((v) => {
                if (v instanceof File) {
                    formData.append(`${key}[]`, v);
                } else if (typeof v === "object" && v !== null) {
                    formData.append(`${key}[]`, JSON.stringify(v));
                } else {
                    formData.append(`${key}[]`, v as any);
                }
            });
        } else if (value instanceof File) {
            formData.append(key, value);
        } else if (typeof value === "object" && value !== null) {
            formData.append(key, JSON.stringify(value));
        } else {
            formData.append(key, value as any);
        }
    });

    return formData;
}

export const success = (message: string) => toast.success(message, {
    position: "top-right",
    autoClose: 3000,
    theme: "colored",
})

export const error = (message: string) => toast.error(message, {
    position: "top-right",
    autoClose: 5000,
    theme: "colored",
})



export async function logout() {
    await api.post('logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
}

export const formatVND = (amount: any) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);


export const formatDate = (dateInput: string | Date, options?: Intl.DateTimeFormatOptions): string => {
    if (!dateInput) return '';

    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) return ''; // Nếu date không hợp lệ, trả về rỗng

    const defaultOptions: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    };

    return new Intl.DateTimeFormat('vi-VN', options || defaultOptions).format(date);
};

export const isFile = (v: unknown): v is File => v instanceof File;

export const isFileArray = (arr: unknown): arr is File[] => Array.isArray(arr) && arr.every(isFile);

interface ErrorResponse {
    message?: string; // message chung
    errors?: Record<string, string[]>; // lỗi từng field
}


export const showErrors = (err: unknown, showAll = false): void => {
    const errorObj = (err as { response?: { data?: ErrorResponse } } | ErrorResponse) || {};

    const data: ErrorResponse = (errorObj as any).response?.data || (errorObj as ErrorResponse);

    let message = 'Đã xảy ra lỗi không xác định';

    if (data.errors) {
        const allErrors = Object.values(data.errors).flat();
        message = showAll ? allErrors.join(', ') : allErrors[0];
    } else if (data.message) {
        message = data.message;
    }

    error(message);
};

/**
 * Lấy ngôn ngữ hiện tại
 */
export const getCurrentLanguage = (): string => i18n.language || 'vi';

/**
 * Lấy giá trị field theo ngôn ngữ
 * @param item object - dữ liệu cần hiển thị
 * @param field string - tên field gốc (ví dụ 'name', 'address')
 */
export const getFieldByLang = (item: any, field: string): string => {
    const lang = getCurrentLanguage();

    const fieldMap: Record<string, string> = {
        vi: item[`${field}`],
        en: item[`${field}_en`],
        zh: item[`${field}_zh`]
    };

    return fieldMap[lang] || item[field] || '';
};

export const getTextByLang = (field: string): string => {
    const lang = getCurrentLanguage();

    switch (lang) {
        case "en":
            return `${field}_en`;
        case "zh":
            return `${field}_zh`;
        default:
            return field;
    }
};


export const multiLangItem = (item: MultiLangItem): string => {
    const lang = getCurrentLanguage();
    const map: Record<string, string> = {
        vi: item.vi,
        en: item.en,
        zh: item.cn,
    };
    return map[lang] || item.vi;
}
